import { useEffect, useState } from 'react';
import { NudosButton } from '../../../NudosComponents';
import {
  displayErrorNotification,
  displaySuccessNotification
} from '../../../../utils/displayNudosStandardNotifications';
import { updateAdditionalServiceStatus } from '../../../../services/services';
import {
  IadditionalServiceDataForToolLogistics,
  LS_ADDITIONAL_SERVICE_NOT_COMPLETE_REASON,
  LS_ADDITIONAL_SERVICE_NOT_COMPLETE_REASON_ID,
  CONFIG_STATUS_ID
} from '../../../../types/product.configuration.types';
import {
  ADDITIONAL_SERVICE_CLASS_TYPE,
  ADDITIONAL_SERVICE_EVIDENCE_FIELD_NAME_ID,
  ADDITIONAL_SERVICE_STATUS_MODAL_VALUES
} from '../../../../types/product.configuration.types';
import { getAdditionalServiceDisplayElements } from '../../../../utils/logisticServices.utils';
import NotCompletedAdditionalServiceFields from './NotCompletedAdditionalServiceFields/NotCompletedAdditionalServiceFields';
import CompletedAdditionalServiceFields from './CompletedAdditionalServiceFields/CompletedAdditionalServiceFields';
import { sendRequireConfirmConfigurationEmail, updateAdditionalConfigurationStatus } from '../../../../services/orders';
import { hasConfigRequiredAdditionalAction } from '../../../../utils/configurations';
import './AdditionalServiceStatusModal.scss';

const AdditionalServiceStatusModalContent = ({
  type,
  additionalServiceData,
  closeModalCallback,
  updateDataCallback,
  isConfiguration,
  orderId
}: {
  type: ADDITIONAL_SERVICE_STATUS_MODAL_VALUES;
  additionalServiceData: IadditionalServiceDataForToolLogistics;
  closeModalCallback: () => void;
  updateDataCallback: () => Promise<void>;
  isConfiguration?: boolean;
  orderId?: string | null;
}) => {
  const [fileToUpload, setFileToUpload] = useState<File>();
  const [csvFileToUpload, setCsvFileToUpload] = useState<File>();
  const [loading, setLoading] = useState<boolean>(false);
  const [notCompletedData, setNotCompletedData] = useState<InotCompletedData>();
  const [dataWasUpdated, setDataWasUpdated] = useState<boolean>(false);
  const [evidenceComments, setEvidenceComments] = useState<string>('');

  const { id: logisticAdditionalServiceProductId, status } = additionalServiceData;
  const {
    configurationImageUrls,
    imageUrl: additionalServiceImageUrl,
    notCompletedReasonId: initialNotCompletedReasonId,
    otherReasonComment: initialOtherReason
  } = status || {};

  const imageUrl = configurationImageUrls?.[0] || additionalServiceImageUrl || undefined;

  const isCompletedModal = type === ADDITIONAL_SERVICE_STATUS_MODAL_VALUES.COMPLETED;

  const incompleteOtherReason =
    notCompletedData?.logisticAdditionalServiceNotCompletedReasonId ===
      LS_ADDITIONAL_SERVICE_NOT_COMPLETE_REASON_ID.OTHER &&
    (!notCompletedData?.otherReasonComment || +notCompletedData.otherReasonComment?.length <= 5);

  const incompleteDataForNotCompletedService =
    !isCompletedModal && (!notCompletedData?.logisticAdditionalServiceNotCompletedReasonId || incompleteOtherReason);

  const currentFileUrl =
    type === ADDITIONAL_SERVICE_STATUS_MODAL_VALUES.SEE_NOT_COMPLETED_REASONS ? imageUrl : undefined;

  const configurationIsHashId = additionalServiceData.configClassName === ADDITIONAL_SERVICE_CLASS_TYPE.HASH_ID;
  const showTextArea =
    isCompletedModal && additionalServiceData.configClassName === ADDITIONAL_SERVICE_CLASS_TYPE.CREATE_ADMIN;

  const completedModalIsDisabled = () => {
    if (configurationIsHashId) return !csvFileToUpload;
    return !fileToUpload && !currentFileUrl;
  };

  const disableButton = () => {
    if (!dataWasUpdated) return true;
    return isCompletedModal ? completedModalIsDisabled() : incompleteDataForNotCompletedService;
  };

  const isButtonDisabled = disableButton();

  const configStatusId = isCompletedModal ? CONFIG_STATUS_ID.COMPLETED : CONFIG_STATUS_ID.NOT_COMPLETED;

  const getDataByType = () => {
    const { text } = getAdditionalServiceDisplayElements(additionalServiceData);
    switch (type) {
      case ADDITIONAL_SERVICE_STATUS_MODAL_VALUES.COMPLETED:
        return {
          subtitle: `Sube una evidencia ${
            isConfiguration ? 'de la configuración' : 'del servicio'
          } ${text} para marcarlo como completado`
        };
      default:
        return {
          subtitle: `Indica el motivo por el cual no se completó ${
            isConfiguration ? 'la configuración' : 'el servicio'
          } ${text}`
        };
    }
  };

  const uploadProofFile = (file?: File) => {
    setDataWasUpdated(true);
    setFileToUpload(file);
  };

  const uploadCsvFile = (file?: File) => {
    setDataWasUpdated(true);
    setCsvFileToUpload(file);
  };

  const submitModal = async () => {
    if (isButtonDisabled || !logisticAdditionalServiceProductId) return;
    if (!fileToUpload && !imageUrl) return;
    try {
      setLoading(true);
      const { logisticAdditionalServiceNotCompletedReasonId, otherReasonComment } = notCompletedData || {};
      const isNotOtherReason =
        notCompletedData?.logisticAdditionalServiceNotCompletedReasonId !==
        LS_ADDITIONAL_SERVICE_NOT_COMPLETE_REASON_ID.OTHER;
      const newOtherReasonComment = isCompletedModal || isNotOtherReason ? null : otherReasonComment;
      const body = {
        logisticAdditionalServiceProductId,
        configStatusId,
        logisticAdditionalServiceNotCompletedReasonId: isCompletedModal
          ? null
          : logisticAdditionalServiceNotCompletedReasonId,
        otherReasonComment: newOtherReasonComment,
        imageUrl: !fileToUpload && !isCompletedModal && imageUrl ? imageUrl : undefined
      };
      await updateAdditionalServiceStatus(body, fileToUpload);
      await updateDataCallback();
      closeModalCallback();
      displaySuccessNotification({
        customJSXMessage: <>{`${isConfiguration ? 'Configuración' : 'Servicio'} adicional actualizado con éxito`}</>
      });
    } catch {
      displayErrorNotification();
    } finally {
      setLoading(false);
    }
  };

  const getConfigEvidence = () => {
    if (isCompletedModal) {
      return evidenceComments
        ? [{ fieldNameId: ADDITIONAL_SERVICE_EVIDENCE_FIELD_NAME_ID.EVIDENCE_COMMENTS, fieldValue: evidenceComments }]
        : undefined;
    }
    return [
      { fieldNameId: 1, fieldValue: LS_ADDITIONAL_SERVICE_NOT_COMPLETE_REASON.OTHER },
      {
        fieldNameId: ADDITIONAL_SERVICE_EVIDENCE_FIELD_NAME_ID.EVIDENCE_COMMENTS,
        fieldValue: notCompletedData?.otherReasonComment
      }
    ];
  };

  const submitConfiguration = async () => {
    if (isButtonDisabled) return;
    setLoading(true);
    const newData = {
      uuid: additionalServiceData.uuid,
      configStatusId,
      configEvidence: getConfigEvidence()
    };
    const formData = new FormData();
    fileToUpload && formData.append('files', fileToUpload);
    csvFileToUpload && formData.append('files', csvFileToUpload);
    formData.append('newData', JSON.stringify(newData));
    try {
      await updateAdditionalConfigurationStatus(formData);
      if (
        configStatusId === CONFIG_STATUS_ID.COMPLETED &&
        hasConfigRequiredAdditionalAction(additionalServiceData.configClassName)
      ) {
        await sendRequireConfirmConfigurationEmail(Number(orderId));
      }
      await updateDataCallback();
      closeModalCallback();
      displaySuccessNotification({
        customJSXMessage: <>{`${isConfiguration ? 'Configuración' : 'Servicio'} adicional actualizado con éxito`}</>
      });
    } catch {
      displayErrorNotification();
    } finally {
      setLoading(false);
    }
  };

  const { subtitle } = getDataByType();

  const checkForNotCompletedDataChanges = () => {
    const { otherReasonComment, logisticAdditionalServiceNotCompletedReasonId } = notCompletedData || {};
    if (dataWasUpdated || !logisticAdditionalServiceNotCompletedReasonId) return;
    const otherReasonIsDifferent =
      initialOtherReason && otherReasonComment ? initialOtherReason !== otherReasonComment : false;
    const notCompletedReasonIdIsDifferent =
      initialNotCompletedReasonId !== logisticAdditionalServiceNotCompletedReasonId;
    const dataHasChanged = otherReasonIsDifferent || notCompletedReasonIdIsDifferent;
    if (dataHasChanged) setDataWasUpdated(true);
  };

  useEffect(() => {
    if (notCompletedData || !initialNotCompletedReasonId) return;
    const initialNotCompleteData = {
      logisticAdditionalServiceNotCompletedReasonId: initialNotCompletedReasonId,
      otherReasonComment: initialOtherReason || undefined
    };
    setNotCompletedData(initialNotCompleteData);
  }, [initialNotCompletedReasonId, initialOtherReason]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    checkForNotCompletedDataChanges();
  }, [notCompletedData]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`additionalServiceStatusModalContent ${type}`}>
      <div className={`subtitle orangeText${showTextArea ? ' hasTextArea' : ''}`}>{subtitle}</div>
      {isCompletedModal && (
        <CompletedAdditionalServiceFields
          additionalServiceData={additionalServiceData}
          evidenceComments={evidenceComments}
          setEvidenceComments={setEvidenceComments}
          uploadProofFile={uploadProofFile}
          uploadCsvFile={uploadCsvFile}
        />
      )}
      {!isCompletedModal && (
        <NotCompletedAdditionalServiceFields
          type={type}
          updateDataCallback={setNotCompletedData}
          initialData={notCompletedData}
          uploadProofFile={uploadProofFile}
          additionalServiceData={additionalServiceData}
          isConfiguration={isConfiguration}
        />
      )}
      <div className="verificationQuestion orangeText">¿Seguro que deseas continuar?</div>
      <NudosButton
        componentSize="small"
        buttonText={`Marcar como ${!isCompletedModal ? 'no' : ''} ${isConfiguration ? 'completada' : 'completado'}`}
        handleClick={isConfiguration ? submitConfiguration : submitModal}
        isButtonDisabled={isButtonDisabled}
        isButtonLoading={loading}
      />
    </div>
  );
};

export default AdditionalServiceStatusModalContent;

export interface InotCompletedData {
  logisticAdditionalServiceNotCompletedReasonId: number;
  otherReasonComment?: string;
}
